/**
 * Seletor de permissões finas por "pastinha" (módulo), ligado ao react-hook-form.
 * Guarda `string[]` de keys, no mesmo contrato do RHFMultiAutocomplete.
 *
 * As pastinhas vêm do catálogo do backend (/permissions/catalog). A visão é
 * filtrada pelo lado do papel selecionado (staff x cliente). staff_admin não
 * escolhe nada: tem acesso total (bypass no servidor).
 *
 * `travadas` são as permissões que o usuário recebe do cargo (15/10/2026): aparecem
 * marcadas e sem como desmarcar, porque quem decide é o cargo. O campo guarda só as
 * demais, que são os adicionais.
 *
 * `areasDoAlvo` são as áreas do cargo ou do usuário em edição (24/10/2026). Módulo de
 * área não marcada fica desabilitado, porque a permissão dele não valeria, e o que
 * estava marcado nele é limpo à vista, antes de salvar. O servidor faz o mesmo
 * descarte; aqui é para quem concede ver o que vai acontecer.
 */
/* eslint-disable react-refresh/only-export-components -- hook usePermissionCatalog co-locado com o campo propositalmente */
import { useEffect, useMemo, useState } from 'react'
import { useController, useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ChevronDown, ChevronUp, Folder, Search, ShieldCheck } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { http } from '../../core/api/client'
import { CATALOG_STALE_TIME } from '../../core/query/queryClient'
import { useDebounced } from '../useDebounced'
import { LEAF_RADIUS_SM } from '../../styles/theme'
import type { PermissionCatalog, PermissionModule, PermissionSide } from '../../core/api/types'
import { useI18n } from '@/core/i18n/useI18n'
import { permissionLabel, permissionModuleLabel } from '../permissions'

const SEM_TRAVADAS: readonly string[] = []

/** Catálogo de permissões (estático por sessão). */
export function usePermissionCatalog() {
  return useQuery({
    queryKey: ['permissions', 'catalog'],
    queryFn: () => http.get<PermissionCatalog>('/permissions/catalog'),
    staleTime: CATALOG_STALE_TIME,
  })
}

/** Mostra a permissão/pastinha no lado escolhido (o próprio lado ou `both`). */
function onSide(side: PermissionSide, want: PermissionSide): boolean {
  return side === want || side === 'both'
}

/** O módulo pertence a alguma das áreas do alvo? Módulo sem área é compartilhado e
 *  vale sempre; sem `areasDoAlvo`, como no portal, não há filtro. */
function moduloNaArea(module: PermissionModule, areasDoAlvo: readonly string[] | undefined): boolean {
  if (!areasDoAlvo || !module.areas || module.areas.length === 0) return true
  return module.areas.some((a) => areasDoAlvo.includes(a))
}

function ModuleCard({
  module,
  perms,
  selected,
  travadas,
  aberto,
  foraDaArea = false,
  onToggle,
  onToggleAll,
  onToggleAberto,
}: {
  module: PermissionModule
  /** Permissões já filtradas por lado E por busca. */
  perms: PermissionModule['permissions']
  /** As marcadas, as do cargo incluídas. */
  selected: Set<string>
  travadas: Set<string>
  aberto: boolean
  /** O alvo não tem a área do módulo: nada aqui pode ser marcado. */
  foraDaArea?: boolean
  onToggle: (key: string) => void
  onToggleAll: (keys: string[], next: boolean) => void
  onToggleAberto: () => void
}) {
  const { t } = useI18n()
  if (perms.length === 0) return null
  const keys = perms.map((p) => p.key)
  const selCount = keys.filter((k) => selected.has(k)).length
  const allChecked = selCount === keys.length
  const indeterminate = selCount > 0 && !allChecked
  // A caixa da pastinha só alcança o que é do usuário; as do cargo ficam.
  const editaveis = foraDaArea ? [] : keys.filter((k) => !travadas.has(k))
  const rotulo = permissionModuleLabel(t, module.key, module.label)
  const nomesDasAreas = (module.areas ?? []).map((a) => t(`areas.${a}.nome`))

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: LEAF_RADIUS_SM, p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Checkbox
          size="small"
          checked={allChecked}
          indeterminate={indeterminate}
          disabled={editaveis.length === 0}
          onChange={() => onToggleAll(editaveis, !editaveis.every((k) => selected.has(k)))}
          inputProps={{ 'aria-label': rotulo }}
          sx={{ p: 0.5 }}
        />
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          <Folder size={16} />
        </Box>
        <Typography variant="subtitle2" sx={{ flex: 1, color: foraDaArea ? 'text.disabled' : undefined }}>
          {rotulo}
        </Typography>
        {nomesDasAreas.map((nome) => (
          <Chip key={nome} size="small" label={nome} sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' } }} />
        ))}
        {/* O contador fica no cabeçalho, e não dentro do corpo: módulo recolhido
            nunca esconde o fato de haver coisa marcada lá dentro. */}
        <Chip size="small" variant="outlined" label={`${selCount}/${keys.length}`} />
        <IconButton
          size="small"
          onClick={onToggleAberto}
          aria-expanded={aberto}
          aria-controls={`perm-mod-${module.key}`}
          aria-label={t(aberto ? 'form.permissionPicker.collapse' : 'form.permissionPicker.expand', {
            module: rotulo,
          })}
        >
          {aberto ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>
      </Box>
      <Collapse in={aberto}>
        {foraDaArea && (
          <Typography variant="caption" component="p" sx={{ color: 'text.secondary', pl: 4.5, mt: 0.5 }}>
            {t('form.permissionPicker.semArea', { areas: nomesDasAreas.join(t('form.permissionPicker.ouAreas')) })}
          </Typography>
        )}
        <Box
          id={`perm-mod-${module.key}`}
          sx={{
            mt: 0.5,
            pl: 4.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 2,
          }}
        >
          {perms.map((p) => {
            const travada = travadas.has(p.key)
            return (
              <FormControlLabel
                key={p.key}
                disabled={travada || foraDaArea}
                control={
                  <Checkbox size="small" checked={selected.has(p.key)} onChange={() => onToggle(p.key)} sx={{ p: 0.5 }} />
                }
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" component="span">{permissionLabel(t, p.key, p.label)}</Typography>
                    {travada && (
                      <Chip
                        size="small"
                        variant="outlined"
                        color="primary"
                        label={t('form.permissionPicker.doCargo')}
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.6875rem' } }}
                      />
                    )}
                  </Box>
                }
              />
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

/**
 * Campo de permissões. `side` fixa o lado (ex.: portal = 'customer'); se omitido,
 * o lado é derivado do papel selecionado no formulário (campo `role`).
 */
export function RHFPermissionPicker({
  name,
  side,
  buscavel = true,
  preSelecionarPadrao = false,
  travadas = SEM_TRAVADAS,
  areasDoAlvo,
}: {
  name: string
  side?: 'staff' | 'customer'
  /** Desliga a barra de busca e as ações em bloco, para usos com poucas pastinhas. */
  buscavel?: boolean
  /** Só na CRIAÇÃO: pré-seleciona o conjunto padrão do papel escolhido. */
  preSelecionarPadrao?: boolean
  /** As do cargo: marcadas, travadas e fora do valor do campo. */
  travadas?: readonly string[]
  /** As áreas do cargo ou do usuário em edição. Ausente, não filtra por área. */
  areasDoAlvo?: readonly string[]
}) {
  const { t } = useI18n()
  const { data, isLoading, isError } = usePermissionCatalog()
  const [q, setQ] = useState('')
  const busca = useDebounced(q.trim(), 200)
  const [recolhidos, setRecolhidos] = useState<Set<string>>(() => new Set())
  const termo = busca.toLocaleLowerCase()
  const watchedRole = useWatch({ name: 'role' }) as string | undefined
  const isSuperRole = watchedRole === 'staff_admin'
  const effectiveSide: 'staff' | 'customer' =
    side ?? (watchedRole?.startsWith('staff') ? 'staff' : 'customer')

  const { field } = useController({ name })
  const selectedList: string[] = useMemo(() => (field.value as string[]) ?? [], [field.value])
  const travadasSet = useMemo(() => new Set(travadas), [travadas])
  // O que aparece marcado: as do campo mais as do cargo.
  const selected = useMemo(() => new Set([...selectedList, ...travadas]), [selectedList, travadas])

  const visibleModules = useMemo(
    () => (data?.modules ?? []).filter((m) => onSide(m.side, effectiveSide)),
    [data, effectiveSide],
  )
  const visibleKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const m of visibleModules) for (const p of m.permissions) if (onSide(p.side, effectiveSide)) keys.add(p.key)
    return keys
  }, [visibleModules, effectiveSide])

  // Ao trocar de lado (papel), poda keys que não pertencem mais ao lado visível —
  // evita levar permissão de staff para um usuário de cliente e vice-versa.
  useEffect(() => {
    if (!data) return
    const pruned = selectedList.filter((k) => visibleKeys.has(k))
    if (pruned.length !== selectedList.length) field.onChange(pruned)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSide, data])

  // Desmarcar uma área limpa, à vista, o que estava marcado nos módulos dela. Só
  // depois de o catálogo chegar, que é quem diz a área de cada módulo.
  const chaveDasAreas = areasDoAlvo ? [...areasDoAlvo].sort().join(',') : null
  useEffect(() => {
    if (!data || chaveDasAreas === null) return
    const foraDaArea = new Set(
      visibleModules.filter((m) => !moduloNaArea(m, areasDoAlvo)).flatMap((m) => m.permissions.map((p) => p.key)),
    )
    const pruned = selectedList.filter((k) => !foraDaArea.has(k))
    if (pruned.length !== selectedList.length) field.onChange(pruned)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDasAreas, data])

  // Ao CRIAR, pré-seleciona o conjunto padrão do papel escolhido. O servidor só
  // aplica o default quando a chave `permissions` está ausente, e este formulário
  // sempre a envia: sem esta pré-seleção, o usuário criado pela interface entrava
  // com a barra lateral vazia.
  //
  // Reagir ao PAPEL, e não à montagem, é o que faz trocar o papel trazer o conjunto
  // certo. Só vale na criação: em edição, o que está salvo é a verdade.
  useEffect(() => {
    if (!preSelecionarPadrao || !data || !watchedRole) return
    const padrao = data.defaults?.[watchedRole]
    if (!padrao) return
    field.onChange(padrao.filter((k) => visibleKeys.has(k)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelecionarPadrao, data, watchedRole])

  // Filtra por rótulo do MÓDULO ou da PERMISSÃO, sobre a string já traduzida e não
  // sobre a key: quem busca "descarga" precisa achar `unloading.view`. Módulo que
  // casa pelo nome mostra a pastinha inteira; senão, só as permissões que casaram.
  const grupos = useMemo(() => {
    const combina = (texto: string) => texto.toLocaleLowerCase().includes(termo)
    return visibleModules.flatMap((m) => {
      const perms = m.permissions.filter((p) => onSide(p.side, effectiveSide))
      if (!perms.length) return []
      if (!termo) return [{ modulo: m, perms }]
      if (combina(permissionModuleLabel(t, m.key, m.label))) return [{ modulo: m, perms }]
      const casaram = perms.filter((p) => combina(permissionLabel(t, p.key, p.label)))
      return casaram.length ? [{ modulo: m, perms: casaram }] : []
    })
  }, [visibleModules, effectiveSide, termo, t])

  // Marcar e desmarcar em bloco agem só sobre o que está NA TELA. Agir sobre o
  // catálogo inteiro com busca ativa concederia permissão escondida, o que é
  // exatamente a armadilha que este tipo de botão costuma criar.
  const chavesVisiveis = useMemo(
    () => grupos.filter((g) => moduloNaArea(g.modulo, areasDoAlvo)).flatMap((g) => g.perms.map((p) => p.key)),
    [grupos, areasDoAlvo],
  )
  const marcadasVisiveis = chavesVisiveis.filter((k) => selected.has(k)).length

  function setKeys(next: Set<string>) {
    // O campo guarda só as do usuário: as do cargo saem antes de gravar no form.
    field.onChange([...next].filter((k) => !travadasSet.has(k)))
  }
  function toggle(key: string) {
    if (travadasSet.has(key)) return
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setKeys(next)
  }
  function toggleAll(keys: string[], on: boolean) {
    const next = new Set(selected)
    keys.filter((k) => !travadasSet.has(k)).forEach((k) => (on ? next.add(k) : next.delete(k)))
    setKeys(next)
  }

  if (isSuperRole) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: LEAF_RADIUS_SM,
          p: 1.5,
          color: 'text.secondary',
        }}
      >
        <ShieldCheck size={18} />
        <Typography variant="body2">
          {t('form.permissionPicker.adminFullAccess')}
        </Typography>
      </Box>
    )
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2">{t('form.permissionPicker.loading')}</Typography>
      </Box>
    )
  }
  if (isError) {
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }}>
        {t('form.permissionPicker.loadFailed')}
      </Typography>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {buscavel && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 0.5 }}>
          <TextField
            size="small"
            sx={{ flex: '1 1 240px', maxWidth: 360 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('form.permissionPicker.search')}
            inputProps={{ 'aria-label': t('form.permissionPicker.search') }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={t('form.permissionPicker.totalSelected', {
              count: marcadasVisiveis,
              total: chavesVisiveis.length,
            })}
          />
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => toggleAll(chavesVisiveis, true)}>
            {t('form.permissionPicker.selectAll')}
          </Button>
          <Button size="small" color="inherit" onClick={() => toggleAll(chavesVisiveis, false)}>
            {t('form.permissionPicker.clearAll')}
          </Button>
        </Box>
      )}

      {grupos.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary', py: 2 }}>
          {t('form.permissionPicker.noResults')}
        </Typography>
      )}

      {grupos.map(({ modulo, perms }) => (
        <ModuleCard
          key={modulo.key}
          module={modulo}
          perms={perms}
          selected={selected}
          travadas={travadasSet}
          // Com termo de busca ativo, força expandido: recolher o que acabou de
          // casar com a busca esvaziaria o próprio resultado.
          aberto={Boolean(termo) || !recolhidos.has(modulo.key)}
          foraDaArea={!moduloNaArea(modulo, areasDoAlvo)}
          onToggle={toggle}
          onToggleAll={toggleAll}
          onToggleAberto={() =>
            setRecolhidos((anterior) => {
              const proximo = new Set(anterior)
              if (proximo.has(modulo.key)) proximo.delete(modulo.key)
              else proximo.add(modulo.key)
              return proximo
            })
          }
        />
      ))}
    </Box>
  )
}
